import { useState } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import LockResetIcon from "@mui/icons-material/LockReset";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { API } from "../../queries/api";
import BACKGROUND_IMAGE from "../../imgs/BRIDGES_SIGNIN_BACKGROUND.png";

export default function ForgotPassword() {
	const navigate = useNavigate();
	const [error, setError] = useState("");
	const [sent, setSent] = useState(false);
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError("");
		const form = new FormData(event.currentTarget);
		const email = (form.get("email") as string)?.trim();
		if (!email) {
			setError("Please enter your email address");
			return;
		}
		setLoading(true);
		try {
			await API.postSK("login/password/reset", { email }, { storeBearer: false });
			setSent(true);
		} catch (e: any) {
			// Backend returns 400 for unknown/invalid email. To avoid account
			// enumeration we show the same confirmation; only surface real
			// (network/server) failures as an error.
			if (e?.response?.status === 400) {
				setSent(true);
			} else {
				setError("Could not send the reset email. Please try again.");
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<Grid container component="main" sx={{ height: "100vh" }}>
			<CssBaseline />
			<Grid
				item
				xs={false}
				sm={4}
				md={7}
				sx={{
					backgroundImage: `url(${BACKGROUND_IMAGE})`,
					backgroundRepeat: "no-repeat",
					backgroundColor: (t) => (t.palette.mode === "light" ? t.palette.grey[50] : t.palette.grey[900]),
					backgroundSize: "cover",
					backgroundPosition: "center",
				}}
			/>
			<Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square sx={{ display: "flex", alignItems: "center" }}>
				<Box sx={{ my: 4, mx: 4, display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
					<Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
						<LockResetIcon />
					</Avatar>
					<Typography component="h1" variant="h5">
						Reset password
					</Typography>
					{sent ? (
						<Box sx={{ mt: 3, maxWidth: 400, textAlign: "center" }}>
							<Typography variant="body1" sx={{ mb: 2 }}>
								If an account exists for that email, we've sent a link to reset your password. Please check your inbox.
							</Typography>
							<Link component="button" type="button" variant="body2" onClick={() => navigate("/login")}>
								Back to sign in
							</Link>
						</Box>
					) : (
						<Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, maxWidth: 400 }}>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
								Enter the email address linked to your account and we'll send you a reset link.
							</Typography>
							<TextField margin="normal" required fullWidth id="email" label="Email Address" name="email" autoComplete="email" autoFocus />
							<Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3, mb: 2 }}>
								{loading ? "Sending..." : "Send reset link"}
							</Button>
							<Box sx={{ textAlign: "center" }}>
								<Link component="button" type="button" variant="body2" onClick={() => navigate("/login")}>
									Back to sign in
								</Link>
							</Box>
							{error && (
								<Typography variant="body2" color="error" sx={{ mt: 2 }}>
									{error}
								</Typography>
							)}
						</Box>
					)}
				</Box>
			</Grid>
		</Grid>
	);
}
